import * as React from 'react';
import { Box } from '@mui/material';

/** Where an order or registration was placed. Null for ones made before this was recorded (21 Oct 2026). */
export type OrderSource = 'ios' | 'android' | 'web';

const chip = {
    fontSize: '11.5px', borderRadius: '9px', padding: '1px 8px', whiteSpace: 'nowrap', lineHeight: '18px',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
} as const;

/** Short labels next to NC / RC (Ariel, 25 Oct 2026); the tooltip spells them out. */
const SOURCE_LABEL: Record<OrderSource, string> = { ios: 'iOS', android: 'Android', web: 'Web' };
const SOURCE_TITLE: Record<OrderSource, string> = { ios: 'Ordered in the iOS app', android: 'Ordered in the Android app', web: 'Ordered on the website' };

const PhoneIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="7" y="2.5" width="10" height="19" rx="2.2" /><path d="M11 18.5h2" />
    </svg>
);
const MonitorIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M9 20h6M12 16v4" />
    </svg>
);

/** The tooltip spelling out an NC / RC chip. Earlier orders count across every Vello venue (Ariel, 25 Oct 2026). */
export const customerTypeTitle = (prev: number, unit: 'order' | 'registration'): string => {
    const what = unit === 'order' ? 'Vello order' : 'Vello Shabbat registration';
    return prev === 0
        ? `New customer · first ${what}`
        : `Returning customer · ${prev} previous ${what}${prev === 1 ? '' : 's'}`;
};

/** Amber "NC" (new customer) / teal "RC n" (returning, n earlier Vello orders at any venue); nothing when prev is unknown. */
export const CustomerTypeChip: React.FC<{ prev?: number | null; unit: 'order' | 'registration' }> = ({ prev, unit }) => {
    if (prev === undefined || prev === null) return null;
    const title = customerTypeTitle(prev, unit);
    if (prev === 0) {
        return <Box component="span" title={title} aria-label={title} sx={{ ...chip, fontWeight: 700, color: '#92400E', bgcolor: '#FEF3C7' }}>NC</Box>;
    }
    return (
        <Box component="span" title={title} aria-label={title} sx={{ ...chip, fontWeight: 700, color: '#03676A', bgcolor: '#DDF4F1' }}>
            RC {prev}
        </Box>
    );
};

/** Grey "iOS" / "Android" / "Web" chip; nothing when the source is unknown - never guessed. Admin only (the server omits it for vendors). */
export const SourceChip: React.FC<{ src?: OrderSource | null }> = ({ src }) => {
    if (!src || !SOURCE_LABEL[src]) return null;
    return (
        <Box component="span" title={SOURCE_TITLE[src]} aria-label={SOURCE_TITLE[src]} sx={{ ...chip, color: '#374151', bgcolor: '#F3F4F6' }}>
            {src === 'web' ? <MonitorIcon /> : <PhoneIcon />}{SOURCE_LABEL[src]}
        </Box>
    );
};

/** The blue chip for one Shabbat registration covering dinner and lunch. */
export const DinnerLunchChip: React.FC = () => (
    <Box component="span" sx={{ ...chip, color: '#1E40AF', bgcolor: '#DBEAFE' }}>Dinner + lunch</Box>
);

/** The chips row under a customer's name. Renders nothing when there is nothing to show. */
export const CustomerChips: React.FC<{
    prev?: number | null; src?: OrderSource | null; unit: 'order' | 'registration'; children?: React.ReactNode;
}> = ({ prev, src, unit, children }) => {
    const hasType = prev !== undefined && prev !== null;
    if (!hasType && !src && !children) return null;
    return (
        <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap', mt: 0.5 }}>
            <CustomerTypeChip prev={prev} unit={unit} />
            <SourceChip src={src} />
            {children}
        </Box>
    );
};
